import { useEffect } from 'react'

/*
  Wires a Google Places Autocomplete onto a single <input> ref. The
  Insites Liquid `modules/website/layout/googlemaps` partial loads the
  Maps JS SDK and exposes `window.googleMapsReady` as a Promise — we
  await that before binding so the autocomplete instance is created
  with a real `google.maps.places` namespace, not a "google is not
  defined" race.

  When the user picks a place, we parse `address_components` into the
  same shape the addon's `address-lookup.js` expects:
    address_1: street_number + route + premise (combined)
    address_2: subpremise / sublocality (combined)
    suburb:    locality / postal_town
    state:     administrative_area_level_1 (short_name)
    postcode:  postal_code
    country:   country (long_name)
  …and call onPlaceSelected with the parsed fields.
*/

export interface ParsedPlace {
  address_1: string
  address_2: string
  suburb: string
  state: string
  postcode: string
  country: string
}

declare global {
  interface Window {
    googleMapsReady?: Promise<void>
    google?: {
      maps?: {
        places?: {
          Autocomplete: new (
            input: HTMLInputElement,
            opts?: {
              componentRestrictions?: { country: string | string[] }
              fields?: string[]
              types?: string[]
            },
          ) => GoogleAutocomplete
        }
      }
    }
  }
}

interface GoogleAutocomplete {
  addListener(event: 'place_changed', cb: () => void): { remove: () => void }
  getPlace(): {
    address_components?: Array<{
      long_name: string
      short_name: string
      types: string[]
    }>
    formatted_address?: string
    geometry?: { location?: { lat(): number; lng(): number } }
  }
  setComponentRestrictions(opts: { country: string | string[] }): void
}

function parseAddressComponents(
  components: Array<{ long_name: string; short_name: string; types: string[] }>,
): ParsedPlace {
  const parsed: ParsedPlace = {
    address_1: '',
    address_2: '',
    suburb: '',
    state: '',
    postcode: '',
    country: '',
  }
  let streetNumber = ''
  let route = ''
  let premise = ''
  let subpremise = ''
  let sublocality = ''

  for (const c of components) {
    const t = c.types[0]
    switch (t) {
      case 'street_number':
        streetNumber = c.short_name
        break
      case 'route':
        route = c.long_name
        break
      case 'premise':
        premise = c.long_name
        break
      case 'subpremise':
        subpremise = c.long_name
        break
      case 'neighborhood':
      case 'sublocality_level_1':
      case 'sublocality':
        sublocality = c.long_name
        break
      case 'locality':
      case 'postal_town':
        parsed.suburb = c.long_name
        break
      case 'administrative_area_level_1':
        parsed.state = c.short_name
        break
      case 'postal_code':
      case 'postal_code_prefix':
        parsed.postcode = c.short_name
        break
      case 'country':
        parsed.country = c.long_name
        break
    }
  }

  parsed.address_1 = [premise, streetNumber, route].filter(Boolean).join(' ').trim()
  parsed.address_2 = [subpremise, sublocality].filter(Boolean).join(' ').trim()
  return parsed
}

export function useGooglePlacesAutocomplete(
  inputRef: React.RefObject<HTMLInputElement | null>,
  onPlaceSelected: (place: ParsedPlace) => void,
  options: { country?: string | string[]; enabled?: boolean } = {},
) {
  const { country = 'au', enabled = true } = options

  useEffect(() => {
    if (!enabled) return
    const input = inputRef.current
    if (!input) return

    let autocomplete: GoogleAutocomplete | null = null
    let cancelled = false

    const ready = window.googleMapsReady
    if (!ready) {
      // Loader partial wasn't included on the page — fail silently rather
      // than throwing; the address fields still work, just without the
      // search-address autocomplete.
      return
    }

    ready.then(() => {
      if (cancelled) return
      const places = window.google?.maps?.places
      if (!places) return
      autocomplete = new places.Autocomplete(input, {
        componentRestrictions: { country },
        fields: ['address_components', 'formatted_address', 'geometry'],
        types: ['address'],
      })
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete!.getPlace()
        if (!place.address_components) return
        const parsed = parseAddressComponents(place.address_components)
        onPlaceSelected(parsed)
      })
    })

    return () => {
      cancelled = true
      // Google Maps doesn't expose a clean teardown for Autocomplete —
      // leaving the listener attached is fine because the input itself
      // unmounts and the GC reclaims it.
    }
  }, [inputRef, onPlaceSelected, country, enabled])
}
